import AgoraManager from '../agora_manager/agora_manager_get_started.js'
import FirebaseService from './FirebaseService.js'
import axios from 'axios'

/**
 * Serviço para gerenciamento de uma sala de telemedicina usando Agora.
 */
class AgoraService {
  /**
   * Construtor do serviço AgoraService.
   * @param {Function} callbackEnterProfessional - Função de callback que é chamada quando 
   * o profissional entra na chamada.
   */
  constructor (callbackEnterProfessional) {
    console.log('CONSTRUCT AgoraService')
    this.agoraAppID = '61cf3781778b4a15a3460bd5ab7814bb'
    this.AgoraManager = null
    this.remotePlayerContainer = null
    this.localPlayerContainer = null

    this.videoOn = true
    this.audioOn = true
    this.blurOn = true

    this.channelParameters = {
      localAudioTrack: null,
      localVideoTrack: null,
      remoteAudioTrack: null,
      remoteVideoTrack: null,
      remoteUid: null
    }
    this.agoraToken = null
    this.patientDropCall = false

    this.FirebaseService = new FirebaseService()
    this.joinParameters = null

    this.callbackEnterProfessional = callbackEnterProfessional

    this.initialize()
  }

  /**
   * Inicializa os containers de vídeo e o gerenciador Agora.
   */
  async initialize () {
    this.remotePlayerContainer = document.createElement('div')
    this.localPlayerContainer = document.createElement('div')
    this.AgoraManager = await AgoraManager(this.handleEvents.bind(this))
  }

  /**
   * Lida com os eventos do Agora.
   * @param {string} eventName - Nome do evento.
   * @param  {...any} args - Argumentos relacionados ao evento.
   */
  handleEvents = (eventName, ...args) => {
    switch (eventName) {
      case 'user-published':
        this._handleUserPublished(args)
        break
      case 'user-unpublished':
        const arg = args[0]
        if (arg._video_muted_ === false && arg._audio_muted_ === false) {
          this.patientDropCall = true
          const value = null
          this.FirebaseService.updateFirebaseRoom(this.joinParameters.cpf, 'funStreamId', value)
          setTimeout(() => {
            this.FirebaseService.updateFirebaseRoom(this.joinParameters.cpf, 'funNome', value)
          }, 50)
          this.callbackEnterProfessional(false)
        }
        break
    }
  }

  /**
   * Cria uma nova sala de vídeo.
   * @param {string} cpf - CPF do usuário.
   * @param {Function} callback - Função de callback para retorno do status.
   */
  async createRoom (cpf, callback) {
    console.warn('[AgoraService] Criar sala')
    this.joinParameters = null

    // Obter token para criar a sala
    this.agoraToken = await this._getAgoraToken(cpf)
    if (!this.agoraToken) {
      callback({ status: false, msg: 'Não foi possível criar a sala.' })
      return
    }

    this.joinParameters = {
      appId: this.agoraAppID,
      channelName: this.agoraToken.canal,
      token: this.agoraToken.token,
      cpf: this.agoraToken.cpf
    }

    const localStreamDiv = document.getElementById('local_stream')
    await this.AgoraManager.join(
      localStreamDiv, this.localPlayerContainer, 
      this.channelParameters, this.joinParameters
    )

    callback({ status: true, agoraToken: this.agoraToken })
  }

  /**
   * Finaliza a transmissão de vídeo.
   * @param {Function} callback - Função de callback para retorno do status.
   */
  async closeVideo (callback) {
    const obj = { status: true }

    if (!this.AgoraManager) {
      callback(obj)
      return
    }

    await this.AgoraManager.leave(this.channelParameters)
    this._removeView('local_video')

    if (this.remotePlayerContainer?.id) {
      this._removeView(this.remotePlayerContainer.id)
    }

    callback(obj)
  }

  /**
   * Ativa ou desativa o vídeo.
   * @param {Function} callback - Função de callback para retorno do status.
   */
  videoOnOff (callback) {
    this.videoOn = !this.videoOn
    this.channelParameters.localVideoTrack.setMuted(!this.videoOn)
    callback({ status: this.videoOn })
  }

  /**
   * Ativa ou desativa o áudio.
   * @param {Function} callback - Função de callback para retorno do status.
   */
  audioOnOff (callback) {
    this.audioOn = !this.audioOn
    this.channelParameters.localAudioTrack.setMuted(!this.audioOn)
    callback({ status: this.audioOn })
  }

  /**
   * Ativa ou desativa o efeito de desfoque de fundo.
   * @param {Function} callback - Função de callback para retorno do status.
   */
  async blurOnOff (callback) {
    this.blurOn = !this.blurOn

    if (this.blurOn) {
      await this.AgoraManager.setBackgroundColor(this.channelParameters)
    } else {
      await this.AgoraManager.disableBackground(this.channelParameters)
    }

    callback({ status: this.blurOn })
  }

  /**
   * Altera o elemento HTML onde o stream de vídeo local será exibido.
   * @async
   * @param {HTMLElement} div - O elemento HTML onde o vídeo local será reproduzido.
   * @param {function} [callback] - Função de callback opcional a ser chamada 
   * após a reprodução do vídeo.
   */
  async changeLocalStreamDiv (div, callback) {
    if (!div) {
      return
    }
    this.channelParameters.localVideoTrack.play(div)
    if (!callback) {
      callback(true)
    }
  }

  /**
   * Obtém o token do Agora para criar a sala.
   * @private
   * @param {string} cpf - CPF do paciente.
   * @returns {Object|null} Objeto com informações do token ou null em caso de erro.
   */
  async _getAgoraToken (cpf) {
    const channel = `CA-pacCpf_${cpf}`
    const objAgora = { canal: channel, uid: channel }

    try {
      const url = `${process.env.VUE_APP_BACKEND_AGORA_ENDPOINT}/telemedicina/gerar-token`
      const response = await axios.post(url, objAgora)
      const token = response.data.token

      // Gerar código de identificação
      const now = new Date()
      const dateBlock = `${now.getFullYear().toString().slice(-1)}${(now.getMonth() + 1).toString().padStart(2, '0')}${now.getDate().toString().padStart(2, '0')}`
      const hourBlock = `${now.getHours().toString().padStart(2, '0')}${now.getMinutes().toString().padStart(2, '0')}${Math.floor(now.getMilliseconds() / 10).toString().padStart(2, '0')}`
      const thirdBlock = Array(3).fill(null).map(() => 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'.charAt(Math.floor(Math.random() * 36))).join('')

      return { token, canal: objAgora.canal, uid: objAgora.uid, cpf, code: `${dateBlock}-${hourBlock}-${thirdBlock}`, bloqueado: 0 }
    } catch (error) {
      console.error('GET TOKEN. ERROR:', error)
      return null
    }
  }

  /**
   * Remove um elemento de vídeo da DOM.
   * @private
   * @param {string} id - ID do elemento a ser removido.
   */
  _removeView (id) {
    console.warn('Removing ' + id + ' Div')
    const div = document.getElementById(id)
    if (div) {
      div.remove()
    }
  }

  /**
   * Lida com o evento de publicação de vídeo de um usuário.
   * @private
   * @param {Array} args - Argumentos relacionados ao evento de publicação do usuário.
   */
  _handleUserPublished (args) {
    if (args[1] === 'video') {
      const remoteUid = args[0].uid.toString() // streamId de quem está entrando
      this.patientDropCall = false
      this.channelParameters.remoteVideoTrack = args[0].videoTrack
      this.channelParameters.remoteAudioTrack = args[0].audioTrack
      this.channelParameters.remoteUid = remoteUid
      this.remotePlayerContainer.id = remoteUid
      this.remotePlayerContainer.style.width = '100vw'
      this.remotePlayerContainer.style.height = '100vh'
      document.getElementById('expand_stream').appendChild(this.remotePlayerContainer)
      this.channelParameters.remoteVideoTrack.play(this.remotePlayerContainer)
      // Reproduz a trilha de áudio, se disponível
      if (this.channelParameters.remoteAudioTrack) {
        this.channelParameters.remoteAudioTrack.setVolume(100);
        this.channelParameters.remoteAudioTrack.play() // Reproduz o áudio
      }
      this.callbackEnterProfessional(true)
    }
  }
}

export default AgoraService
